import { Box, Button, Checkbox, CheckboxGroup, Flex, Radio, RadioGroup, VisuallyHidden } from '@chakra-ui/react';
import { ReactComponent as DeleteIcon } from 'assets/icons/delete.svg';
import { useAppSelector } from 'modules/Editor/store';
import { selectWidgets } from 'modules/Editor/store/infographSelector';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { WidgetId } from 'types/idTypes';
import { TextWidgetData } from 'widgets/TextBasedWidgets/TextWidget/TextWidget.types';
import { getTextWidgetProperties } from 'widgets/TextBasedWidgets/TextWidget/TextWidgetData.helpers';
import { useWidget } from 'widgets/sdk';

import { ANSWERS_LIMIT } from '../../MultipleChoicesWidget.config';
import { ComponentWidgetIdKeys, MultipleChoicesWidgetData } from '../../MultipleChoicesWidget.types';
import { generateNewWidgetMember, updateCorrectAnswers } from '../MultipleChoicesWidgetSideMenuSettings.helpers';
import { MultipleChoicesSettingsTextInput } from './MultipleChoicesSettingsTextInput';

type CorrectSectionProps = {
  isCheckbox: boolean;
};

/**
 * Render the correct answers config on the side panel settings
 */
export const CorrectSection = ({ isCheckbox = false }: CorrectSectionProps) => {
  const { t } = useTranslation('editor_widget_toolbar', {
    keyPrefix: 'multipleChoicesToolbar',
    useSuspense: false,
  });

  const widgets = useAppSelector(selectWidgets);
  const {
    componentWidgetIdMap,
    correctAnswers,
    updateWidget,
    addMemberWidgetToGroup,
    removeMemberWidgetFromGroup,
    memberWidgetIds,
  } = useWidget<MultipleChoicesWidgetData>();

  const questionWidgetId = componentWidgetIdMap[ComponentWidgetIdKeys.QuestionText];
  const answerWidgetIdList = useMemo(
    () => memberWidgetIds.filter((id) => id !== questionWidgetId),
    [memberWidgetIds, questionWidgetId],
  );
  const textWidgets = useMemo(() => answerWidgetIdList.map((wId) => widgets?.[wId]), [answerWidgetIdList, widgets]);
  const answerWidgetTextList = useMemo(
    () => textWidgets.map((widget) => getTextWidgetProperties((widget as TextWidgetData)?.proseMirrorData)?.text || ''),
    [textWidgets],
  );

  const disableAddButton = answerWidgetIdList.length >= ANSWERS_LIMIT;

  const handleCheckboxChange = (value: string[]) => {
    updateWidget({
      correctAnswers: value,
    });
  };

  const handleRadioChange = (value: string) => {
    updateWidget({
      correctAnswers: [value],
    });
  };

  const handleDeleteChoice = useCallback(
    (targetWidgetId: WidgetId) => {
      removeMemberWidgetFromGroup(targetWidgetId, {
        correctAnswers: updateCorrectAnswers(correctAnswers, answerWidgetIdList.indexOf(targetWidgetId)),
      });
    },
    [correctAnswers, answerWidgetIdList, removeMemberWidgetFromGroup],
  );

  const handleAddChoice = useCallback(() => {
    const newMemberWidget = generateNewWidgetMember();
    addMemberWidgetToGroup(newMemberWidget);
  }, [addMemberWidgetToGroup]);

  return (
    <Box w='full'>
      {isCheckbox ? (
        <CheckboxGroup colorScheme='green' defaultValue={[]} value={correctAnswers} onChange={handleCheckboxChange}>
          <Flex gap={2} flexDir={'column'}>
            {answerWidgetIdList.map((widgetId, index) => (
              <Flex key={widgetId} justifyContent={'flex-start'} w={'full'} p={0} m={0} alignItems={'center'}>
                <Checkbox value={index.toString()}>
                  <VisuallyHidden>{answerWidgetTextList[index]}</VisuallyHidden>
                </Checkbox>
                <MultipleChoicesSettingsTextInput widgetId={widgetId} />
                <Button
                  variant='unstyled'
                  minW={5}
                  width={5}
                  height={5}
                  ml={2}
                  onClick={() => handleDeleteChoice(widgetId)}
                >
                  <DeleteIcon width={20} height={20} color='var(--vg-colors-font-500)' />
                </Button>
              </Flex>
            ))}
          </Flex>
        </CheckboxGroup>
      ) : (
        <RadioGroup colorScheme='green' value={correctAnswers[0]} onChange={handleRadioChange}>
          <Flex gap={2} flexDir={'column'}>
            {answerWidgetIdList.map((widgetId, index) => (
              <Flex key={widgetId} justifyContent={'flex-start'} w={'full'} p={0} m={0} alignItems={'center'}>
                <Radio value={index.toString()}>
                  <VisuallyHidden>{answerWidgetTextList[index]}</VisuallyHidden>
                </Radio>
                <MultipleChoicesSettingsTextInput widgetId={widgetId} />
                <Button
                  variant='unstyled'
                  minW={5}
                  width={5}
                  height={5}
                  ml={2}
                  onClick={() => handleDeleteChoice(widgetId)}
                >
                  <DeleteIcon width={20} height={20} color='var(--vg-colors-font-500)' />
                </Button>
              </Flex>
            ))}
          </Flex>
        </RadioGroup>
      )}
      <Button variant='action' isDisabled={disableAddButton} borderRadius={1} w='full' mt={4} onClick={handleAddChoice}>
        {t('settingsSideBar.multipleChoices.addButton')}
      </Button>
    </Box>
  );
};

CorrectSection.displayName = 'CorrectSection';
